import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  Filter, 
  Layers, 
  User, 
  Check, 
  Search,
  Zap,
  AlertCircle,
  Clock,
  CheckCircle2,
  Globe,
  Link as LinkIcon,
  Calendar,
  CalendarDays,
  Infinity,
  Bug,
  ListTodo,
  RefreshCw,
  Bookmark,
  CalendarCheck,
  CalendarX
} from 'lucide-react';
import CustomDatePicker from './CustomDatePicker';
import { useLanguage } from '../context/LanguageContext';

const FilterModal = ({ 
  isOpen, 
  onClose, 
  t,
  // Visibility Controls
  showPriority = true,
  showDateRange = true,
  showStatus = false,
  showPlanning = false,
  showTags = false,
  showCreator = false,
  // Current values from parent
  selectedPriority: currentPriority = 'all',
  selectedStatus: currentStatus = 'all',
  selectedPlanning: currentPlanning = 'all',
  selectedTags: currentTags = 'all',
  selectedCreator: currentCreator = 'all',
  selectedMemberType: currentMemberType = 'creator',
  selectedDateRange: currentDateRange,
  // Callback for applying
  onApply,
  // Data
  members = []
}) => {
  const { lang } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  
  // Internal draft state
  const [localPriority, setLocalPriority] = useState(currentPriority);
  const [localStatus, setLocalStatus] = useState(currentStatus);
  const [localPlanning, setLocalPlanning] = useState(currentPlanning);
  const [localTags, setLocalTags] = useState(currentTags);
  const [localCreator, setLocalCreator] = useState(currentCreator);
  const [localMemberType, setLocalMemberType] = useState(currentMemberType);
  const [localDateRange, setLocalDateRange] = useState(currentDateRange || { type: 'all', start: null, end: null });

  // Sync internal state with external when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setLocalPriority(currentPriority);
      setLocalStatus(currentStatus);
      setLocalPlanning(currentPlanning);
      setLocalTags(currentTags);
      setLocalCreator(currentCreator);
      setLocalMemberType(currentMemberType);
      setLocalDateRange(currentDateRange || { type: 'all', start: null, end: null });
      setSearchTerm(''); 
    }
  }, [isOpen, currentPriority, currentStatus, currentPlanning, currentTags, currentCreator, currentMemberType, currentDateRange]);

  if (!isOpen) return null;

  const handleApply = () => {
    onApply({
      priority: localPriority,
      status: localStatus,
      planning: localPlanning,
      tags: localTags,
      creator: localCreator,
      memberType: localMemberType,
      dateRange: localDateRange
    });
    onClose();
  };

  const handleReset = () => {
    const defaultFilters = {
      priority: 'all',
      status: 'all',
      planning: 'all',
      tags: 'all',
      creator: 'all',
      memberType: 'creator',
      dateRange: { type: 'all', start: null, end: null }
    };
    
    setLocalPriority(defaultFilters.priority);
    setLocalStatus(defaultFilters.status);
    setLocalPlanning(defaultFilters.planning);
    setLocalTags(defaultFilters.tags);
    setLocalCreator(defaultFilters.creator);
    setLocalMemberType(defaultFilters.memberType);
    setLocalDateRange(defaultFilters.dateRange);

    onApply(defaultFilters);
    onClose();
  };

  const filteredMembers = members.filter(m => {
    const nameStr = (m.name || m.email || '').toLowerCase();
    return nameStr.includes(searchTerm.toLowerCase());
  });

  const priorityOptions = [
    { value: 'all', label: t('allPriorities'), icon: Filter },
    { value: 'urgent', label: t('urgent'), icon: Zap, color: '#ef4444' },
    { value: 'high', label: t('high'), icon: AlertCircle, color: '#f59e0b' },
    { value: 'medium', label: t('medium'), icon: Clock, color: '#6366f1' },
    { value: 'low', label: t('low'), icon: CheckCircle2, color: '#10b981' }
  ];

  const statusOptions = [
    { value: 'all', label: t('allStatuses') || 'Todos los Estados', icon: Filter },
    { value: 'bug', label: t('bug'), icon: Bug, color: '#ef4444' },
    { value: 'pending', label: t('pending'), icon: ListTodo, color: '#f59e0b' },
    { value: 'review', label: t('review'), icon: RefreshCw, color: '#6366f1' },
    { value: 'completed', label: t('completed'), icon: CheckCircle2, color: '#10b981' }
  ];

  const planningOptions = [
    { value: 'all', label: t('allPlanning') || 'Todo el Plan', icon: Filter },
    { value: 'planned', label: t('planned') || 'Planificados', icon: CalendarCheck, color: '#10b981' },
    { value: 'unplanned', label: t('unplanned') || 'No Planificados', icon: CalendarX, color: '#ef4444' }
  ];

  const tagOptions = [
    { value: 'all', label: t('allTags'), icon: Layers },
    { value: 'frontend', label: 'Frontend', icon: Layers, color: '#10b981' },
    { value: 'backend', label: 'Backend', icon: Globe, color: '#3b82f6' },
    { value: 'ux', label: 'UX UI', icon: LinkIcon, color: '#f59e0b' }
  ];

  return createPortal(
    <AnimatePresence>
      <motion.div 
        initial={{ opacity: 0 }} 
        animate={{ opacity: 1 }} 
        exit={{ opacity: 0 }} 
        className="fixed inset-0 z-[1000] flex items-center justify-center p-4"
        style={{ pointerEvents: 'auto' }}
      >
        <motion.div 
          initial={{ opacity: 0 }} 
          animate={{ opacity: 1 }} 
          exit={{ opacity: 0 }} 
          className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm"
          onClick={onClose}
        />
        
        <motion.div 
          initial={{ opacity: 0, scale: 0.95, y: 20 }} 
          animate={{ opacity: 1, scale: 1, y: 0 }} 
          exit={{ opacity: 0, scale: 0.95, y: 20 }} 
          className="bg-slate-900 border border-white/10 rounded-[40px] shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh] relative z-10"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-8 border-b border-white/5 bg-slate-900/50">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center border border-primary/20">
                <Filter className="text-primary" size={24} />
              </div>
              <div>
                <h3 className="text-xl font-black text-white leading-tight">{t('filters')}</h3>
                <p className="text-slate-500 text-[10px] font-black uppercase tracking-widest">{t('customizeBoardView')}</p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-slate-400 hover:bg-white/10 hover:text-white transition-all border border-white/5"
            >
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-8 space-y-12 custom-scrollbar bg-slate-900/30">
            
            {/* Priority Section */}
            {showPriority && (
              <section>
                <div className="flex items-center gap-2 mb-6">
                  <AlertCircle size={14} className="text-primary" />
                  <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.25em]">{t('priority')}</h4>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                  {priorityOptions.map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setLocalPriority(opt.value)}
                      className={`flex flex-col items-center justify-center p-4 rounded-2xl border transition-all gap-2 group relative overflow-hidden ${
                        localPriority === opt.value 
                        ? 'bg-primary/10 border-primary/50 shadow-lg' 
                        : 'bg-white/5 border-white/5 hover:border-white/10'
                      }`}
                    >
                      <div 
                        className="w-10 h-10 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110 relative z-10"
                        style={{ backgroundColor: opt.color ? `${opt.color}15` : 'rgba(255,255,255,0.05)' }}
                      >
                        <opt.icon size={20} style={{ color: opt.color || '#94a3b8' }} />
                      </div>
                      <span className={`text-[11px] font-bold relative z-10 ${localPriority === opt.value ? 'text-white' : 'text-slate-400'}`}>
                        {opt.label}
                      </span>
                    </button>
                  ))}
                </div>
              </section>
            )}

            {/* Status Section */}
            {showStatus && (
              <section>
                <div className="flex items-center gap-2 mb-6">
                  <ListTodo size={14} className="text-primary" />
                  <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.25em]">{t('status') || 'Estado'}</h4>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                  {statusOptions.map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setLocalStatus(opt.value)}
                      className={`flex flex-col items-center justify-center p-4 rounded-2xl border transition-all gap-2 group relative overflow-hidden ${
                        localStatus === opt.value 
                        ? 'bg-primary/10 border-primary/50 shadow-lg' 
                        : 'bg-white/5 border-white/5 hover:border-white/10'
                      }`}
                    >
                      <div 
                        className="w-10 h-10 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110 relative z-10"
                        style={{ backgroundColor: opt.color ? `${opt.color}15` : 'rgba(255,255,255,0.05)' }}
                      >
                        <opt.icon size={20} style={{ color: opt.color || '#94a3b8' }} />
                      </div>
                      <span className={`text-[11px] font-bold relative z-10 ${localStatus === opt.value ? 'text-white' : 'text-slate-400'}`}>
                        {opt.label}
                      </span>
                    </button>
                  ))}
                </div>
              </section>
            )}

            {/* Planning Section */}
            {showPlanning && (
              <section>
                <div className="flex items-center gap-2 mb-6">
                  <Bookmark size={14} className="text-primary" />
                  <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.25em]">{t('planning') || 'Planificación'}</h4>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {planningOptions.map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setLocalPlanning(opt.value)}
                      className={`flex items-center gap-3 p-4 rounded-2xl border transition-all group relative overflow-hidden ${
                        localPlanning === opt.value 
                        ? 'bg-primary/10 border-primary/50 shadow-lg' 
                        : 'bg-white/5 border-white/5 hover:border-white/10'
                      }`}
                    >
                      <div 
                        className="w-10 h-10 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110 relative z-10"
                        style={{ backgroundColor: opt.color ? `${opt.color}15` : 'rgba(255,255,255,0.05)' }}
                      >
                        <opt.icon size={20} style={{ color: opt.color || '#94a3b8' }} />
                      </div>
                      <span className={`text-[11px] font-bold relative z-10 ${localPlanning === opt.value ? 'text-white' : 'text-slate-400'}`}>
                        {opt.label}
                      </span>
                    </button>
                  ))}
                </div>
              </section>
            )}

            {/* Tags Section */}
            {showTags && (
              <section>
                <div className="flex items-center gap-2 mb-6">
                  <Layers size={14} className="text-primary" />
                  <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.25em]">{t('tags')}</h4>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {tagOptions.map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setLocalTags(opt.value)}
                      className={`flex items-center justify-center gap-3 p-4 rounded-2xl border transition-all group relative overflow-hidden ${
                        localTags === opt.value 
                        ? 'bg-primary/10 border-primary/50 shadow-lg' 
                        : 'bg-white/5 border-white/5 hover:border-white/10'
                      }`}
                    >
                      <div 
                        className="w-8 h-8 rounded-xl flex items-center justify-center transition-transform group-hover:rotate-12"
                        style={{ backgroundColor: opt.color ? `${opt.color}15` : 'rgba(255,255,255,0.05)' }}
                      >
                        <opt.icon size={14} style={{ color: opt.color || '#94a3b8' }} />
                      </div>
                      <span className={`text-[11px] font-bold ${localTags === opt.value ? 'text-white' : 'text-slate-400'}`}>
                        {opt.label}
                      </span>
                    </button>
                  ))}
                </div>
              </section>
            )}

            {/* Date Range Section */}
            {showDateRange && (
              <section>
                <div className="flex items-center gap-2 mb-6">
                  <Calendar size={14} className="text-primary" />
                  <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.25em]">{t('dateRange')}</h4>
                </div>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { value: 'all', label: t('allTime'), icon: Infinity, color: '#94a3b8' },
                      { value: 'today', label: t('today'), icon: Clock, color: '#f59e0b' },
                      { value: 'week', label: t('thisWeek'), icon: CalendarDays, color: '#6366f1' },
                      { value: 'month', label: t('thisMonth'), icon: Calendar, color: '#10b981' }
                    ].map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => setLocalDateRange({ ...localDateRange, type: opt.value })}
                        className={`flex flex-col items-center justify-center p-3 rounded-2xl border transition-all gap-1.5 group overflow-hidden ${
                          localDateRange.type === opt.value 
                          ? 'bg-primary/10 border-primary/50 shadow-lg' 
                          : 'bg-white/5 border-white/5 hover:border-white/10'
                        }`}
                      >
                        <div 
                          className="w-8 h-8 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110"
                          style={{ backgroundColor: `${opt.color}15` }}
                        >
                          <opt.icon size={16} style={{ color: opt.color }} />
                        </div>
                        <span className={`text-[10px] font-black uppercase tracking-wider ${localDateRange.type === opt.value ? 'text-white' : 'text-slate-500'}`}>
                          {opt.label}
                        </span>
                      </button>
                    ))}
                  </div>

                  <div className={`p-6 rounded-3xl border transition-all ${localDateRange.type === 'custom' ? 'bg-primary/5 border-primary/30' : 'bg-white/5 border-white/5 border-dashed'}`}>
                    <button 
                      onClick={() => setLocalDateRange({ ...localDateRange, type: 'custom' })}
                      className="flex items-center justify-between w-full text-left"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${localDateRange.type === 'custom' ? 'bg-primary text-white' : 'bg-slate-800 text-slate-500'}`}>
                          <Calendar size={16} />
                        </div>
                        <div>
                          <span className={`text-[11px] font-black uppercase tracking-widest ${localDateRange.type === 'custom' ? 'text-white' : 'text-slate-400'}`}>{t('customRange')}</span>
                          {localDateRange.type !== 'custom' && <p className="text-[10px] text-slate-600 font-bold uppercase tracking-tight mt-0.5">Seleccionar fechas específicas</p>}
                        </div>
                      </div>
                      {localDateRange.type === 'custom' && <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center"><Check size={12} className="text-white" /></div>}
                    </button>

                    <AnimatePresence>
                      {localDateRange.type === 'custom' && (
                        <motion.div 
                          initial={{ height: 0, opacity: 0, marginTop: 0 }}
                          animate={{ height: 'auto', opacity: 1, marginTop: 20 }}
                          exit={{ height: 0, opacity: 0, marginTop: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-4 border-t border-white/5">
                            <div className="space-y-2">
                              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1 flex items-center gap-1.5">
                                <div className="w-1 h-1 rounded-full bg-primary" />
                                {t('startDate')}
                              </label>
                              <CustomDatePicker 
                                value={localDateRange.start}
                                onChange={val => setLocalDateRange({ ...localDateRange, start: val })}
                                t={t}
                                lang={lang}
                              />
                            </div>
                            <div className="space-y-2">
                              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1 flex items-center gap-1.5">
                                <div className="w-1 h-1 rounded-full bg-emerald-500" />
                                {t('endDate')}
                              </label>
                              <CustomDatePicker 
                                value={localDateRange.end}
                                onChange={val => setLocalDateRange({ ...localDateRange, end: val })}
                                t={t}
                                lang={lang}
                              />
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </section>
            )}

            {/* Creator Section */}
            {showCreator && (
              <section>
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                       <User size={14} className="text-primary" />
                       <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.25em]">
                         {localMemberType === 'creator' ? t('createdBy') : t('resolvedBy')}
                       </h4>
                    </div>

                    {/* Premium Filter Switch */}
                    <div className="flex items-center bg-white/5 p-1 rounded-xl border border-white/5">
                      <button
                        onClick={() => setLocalMemberType('creator')}
                        className={`px-3 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-wider transition-all relative ${
                          localMemberType === 'creator' ? 'text-white' : 'text-slate-500 hover:text-slate-300'
                        }`}
                      >
                        {localMemberType === 'creator' && (
                          <motion.div
                            layoutId="memberTypePill"
                            className="absolute inset-0 bg-primary/20 border border-primary/30 rounded-lg"
                            transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                          />
                        )}
                        <span className="relative z-10">{t('creator')}</span>
                      </button>
                      <button
                        onClick={() => setLocalMemberType('resolver')}
                        className={`px-3 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-wider transition-all relative ${
                          localMemberType === 'resolver' ? 'text-white' : 'text-slate-500 hover:text-slate-300'
                        }`}
                      >
                        {localMemberType === 'resolver' && (
                          <motion.div
                            layoutId="memberTypePill"
                            className="absolute inset-0 bg-emerald-500/20 border border-emerald-500/30 rounded-lg"
                            transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                          />
                        )}
                        <span className="relative z-10">{t('resolver')}</span>
                      </button>
                    </div>
                  </div>

                  {members.length > 6 && (
                    <div className="relative group">
                      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-primary transition-colors" />
                      <input 
                        type="text"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        placeholder={t('search')}
                        className="bg-white/5 border border-white/5 hover:border-white/10 rounded-xl pl-9 pr-4 py-1.5 text-[10px] text-white focus:outline-none focus:border-primary/50 w-40 transition-all font-bold"
                      />
                    </div>
                  )}
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    onClick={() => setLocalCreator('all')}
                    className={`flex items-center gap-4 p-4 rounded-2xl border transition-all group ${
                      localCreator === 'all' 
                      ? 'bg-primary/10 border-primary/50 shadow-lg' 
                      : 'bg-white/5 border-white/5 hover:border-white/10'
                    }`}
                  >
                    <div className="w-12 h-12 rounded-full bg-slate-800 border border-white/5 flex items-center justify-center text-slate-500 group-hover:text-white transition-colors">
                      <User size={24} />
                    </div>
                    <div className="text-left">
                      <span className={`text-sm font-black block ${localCreator === 'all' ? 'text-white' : 'text-slate-400'}`}>
                        {localMemberType === 'creator' ? t('allCreators') : t('allResolvers')}
                      </span>
                      <span className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">{t('noFilterApplied')}</span>
                    </div>
                    {localCreator === 'all' && <div className="ml-auto w-6 h-6 rounded-full bg-primary flex items-center justify-center shadow-lg shadow-primary/20"><Check size={14} className="text-white" /></div>}
                  </button>

                  {filteredMembers.length > 0 ? (
                    filteredMembers.map((m, index) => (
                      <button
                        key={m.email || m._id || m.id || `member-${index}`}
                        onClick={() => setLocalCreator(String(m.email || m._id || m.id))}
                        className={`flex items-center gap-4 p-4 rounded-2xl border transition-all group ${
                          localCreator === String(m.email || m._id || m.id)
                          ? 'bg-primary/10 border-primary/50 shadow-lg' 
                          : 'bg-white/5 border-white/5 hover:border-white/10'
                        }`}
                      >
                        <div className="relative">
                          {m.avatar ? (
                            <img src={m.avatar} alt={m.name} className="w-12 h-12 rounded-full object-cover border-2 border-white/5 group-hover:border-primary/30 transition-colors" />
                          ) : (
                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-slate-800 to-slate-900 border-2 border-white/5 group-hover:border-primary/30 flex items-center justify-center text-primary text-sm font-black transition-colors">
                              {(m.name || m.email || '?').charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-slate-900 border border-white/5 flex items-center justify-center">
                            <div className="w-2 h-2 rounded-full bg-emerald-500" />
                          </div>
                        </div>
                        <div className="flex flex-col items-start min-w-0 flex-1">
                          <span className={`text-sm font-black truncate w-full ${localCreator === String(m.email || m._id || m.id) ? 'text-white' : 'text-slate-200'}`}>
                            {m.name || m.email?.split('@')[0]}
                          </span>
                          <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider truncate w-full">
                            {m.role ? t(m.role.toLowerCase().startsWith('role_') ? m.role.toLowerCase() : 'role_' + m.role.toLowerCase()).replace('/', ' ') : t('member')}
                          </span>
                        </div>
                        {localCreator === String(m.email || m._id || m.id) && (
                          <div className="ml-auto w-6 h-6 rounded-full bg-primary flex items-center justify-center shadow-lg shadow-primary/20">
                            <Check size={14} className="text-white" />
                          </div>
                        )}
                      </button>
                    ))
                  ) : (
                    <div className="col-span-full py-12 flex flex-col items-center justify-center text-center bg-white/[0.02] border border-dashed border-white/5 rounded-[40px]">
                      <div className="w-12 h-12 rounded-2xl bg-white/5 flex items-center justify-center text-slate-600 mb-4">
                        <Search size={24} />
                      </div>
                      <p className="text-slate-400 text-sm font-bold">{t('noResultsFound')}</p>
                      <p className="text-slate-600 text-[10px] uppercase tracking-widest mt-1">{t('tryDifferentSearch')}</p>
                    </div>
                  )}
                </div>
              </section>
            )}
          </div>

          {/* Footer */}
          <div className="p-8 bg-slate-900 border-t border-white/5 flex items-center justify-between gap-4">
             <button 
              onClick={handleReset}
              className="px-6 py-4 rounded-2xl text-[11px] font-black text-slate-500 hover:text-white hover:bg-white/5 transition-all uppercase tracking-[0.2em]"
             >
               {t('resetFilters')}
             </button>
             <div className="flex gap-4">
                <button 
                  onClick={onClose}
                  className="px-8 py-4 rounded-2xl text-[11px] font-black text-slate-400 hover:text-white transition-all uppercase tracking-[0.2em]"
                >
                  {t('close')}
                </button>
                <button 
                  onClick={handleApply}
                  className="bg-primary hover:bg-blue-600 text-white px-10 py-4 rounded-2xl text-[11px] font-black shadow-xl shadow-primary/20 transition-all uppercase tracking-[0.2em] hover:-translate-y-0.5"
                >
                  {t('applyFilters')}
                </button>
             </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default FilterModal;
